import { useEffect } from "react";

import { styled } from "@linaria/react";
import { useInView } from "react-intersection-observer";

import GridWrapper, { GridAlignContent, GridAlignItems } from "@gx/dls/containers/GridWrapper";
import { SourceResource } from "@gx/ts-contracts/services/source/resources/source_pb";

import SourceCard from "@/pages/sources/components/card/SourceCard";
import SourceCardLoading from "@/pages/sources/components/card/SourceCardLoading";
import SourcesPageEmptyLayout from "@/pages/sources/views/SourcesPageEmptyLayout";

const SourcesPageBodySourcesGridWrapper = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  padding: 12px;

  overflow: auto;
`;

interface SourcesPageBodyGridViewProps {
  sources?: SourceResource[];
  isLoading: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

const SourcesPageBodyGridView = ({
  sources,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: SourcesPageBodyGridViewProps) => {
  const { ref: infiniteScrollRef, inView } = useInView({
    threshold: 0,
    rootMargin: "200px",
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && fetchNextPage) {
      void fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);
  if (isLoading) {
    return (
      <SourcesPageBodySourcesGridWrapper>
        <GridWrapper
          columns="repeat(auto-fill, minmax(320px, 1fr))"
          gap={12}
          alignItems={GridAlignItems.START}
          alignContent={GridAlignContent.START}
          fillWidth
          minHeight={0}
        >
          {Array.from({ length: 10 }).map((_, index) => (
            <SourceCardLoading key={index} />
          ))}
        </GridWrapper>
      </SourcesPageBodySourcesGridWrapper>
    );
  }

  if (sources?.length === 0) {
    return <SourcesPageEmptyLayout />;
  }

  return (
    <SourcesPageBodySourcesGridWrapper>
      <GridWrapper
        columns="repeat(auto-fill, minmax(320px, 1fr))"
        gap={12}
        alignItems={GridAlignItems.START}
        alignContent={GridAlignContent.START}
        fillWidth
        minHeight={0}
      >
        {sources?.map((source) => {
          return <SourceCard key={source.id} source={source} />;
        })}
        {isFetchingNextPage &&
          Array.from({ length: 4 }).map((_, index) => (
            <SourceCardLoading key={`loading-${index}`} />
          ))}
      </GridWrapper>
      <div ref={infiniteScrollRef} style={{ minHeight: 1 }} />
    </SourcesPageBodySourcesGridWrapper>
  );
};

export default SourcesPageBodyGridView;
